/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import {
  CancelRounded as CancelRoundedIcon,
  CheckCircleRounded as CheckCircleRoundedIcon,
  ErrorRounded as ErrorRoundedIcon,
  InfoRounded as InfoRoundedIcon,
  WarningRounded as WarningRoundedIcon,
} from '@mui/icons-material';
import { alpha, Box, Grid, IconButton, Stack, type Theme, Typography } from '@mui/material';
import { NotificationSeverity, type UINotification } from '@poweralpha/common';
import { useCallback } from 'react';
import type { ToastContentProps } from 'react-toastify';
import '../toast.css';

const getSnackNotifSx = (theme: Theme, notif: UINotification) => {
  const color = theme.palette[notif.severity].main;
  return {
    '&:hover': { backgroundColor: alpha(color, theme.palette.mode === 'dark' ? 0.16 : 0.08) },
  };
};

interface Props {
  closeToast: ToastContentProps['closeToast'];
  content: string;
  notification: UINotification;
  handleClick: (notif: UINotification, redirect?: boolean) => void;
}

const SeverityIcon = ({ severity }: { severity: UINotification['severity'] }) => {
  switch (severity) {
    case NotificationSeverity.ERROR:
      return <ErrorRoundedIcon color={severity} fontSize="small" />;
    case NotificationSeverity.WARNING:
      return <WarningRoundedIcon color={severity} fontSize="small" />;
    case NotificationSeverity.SUCCESS:
      return <CheckCircleRoundedIcon color={severity} fontSize="small" />;
    default:
      return <InfoRoundedIcon color={severity} fontSize="small" />;
  }
};

const NotificationToast = ({ content, closeToast, handleClick, notification }: Readonly<Props>) => {
  const handleClose = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      handleClick(notification, false);
      closeToast('manual-dismiss');
    },
    [closeToast, handleClick, notification],
  );

  const handleMouseClick = useCallback(() => {
    handleClick(notification);
    closeToast('notification-click');
  }, [closeToast, handleClick, notification]);

  return (
    <Box
      className="notification-toast-shell"
      onClick={handleMouseClick}
      sx={[(t) => getSnackNotifSx(t, notification)]}
    >
      <Box
        sx={{
          boxSizing: 'border-box',
          height: '100%',
          p: 1.25,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
          <Grid size="grow">
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', minWidth: 0 }}>
                <Box sx={{ alignItems: 'center', display: 'flex', height: 20 }}>
                  <SeverityIcon severity={notification.severity} />
                </Box>
                <Typography
                  noWrap
                  sx={{ fontSize: 15, fontWeight: 'fontWeightBold', lineHeight: '20px' }}
                >
                  {notification.subject}
                </Typography>
              </Stack>
              <Typography
                className="notification-toast-content"
                sx={{ fontSize: 14, lineHeight: 1.35 }}
              >
                {content.length > 150 ? `${content.slice(0, 150)}...` : content}
              </Typography>
              <Typography
                hidden={!notification.message}
                className="notification-toast-message"
                sx={{ color: `${notification.severity}.main`, fontSize: 12 }}
              >
                {notification.severity === NotificationSeverity.ERROR ? 'Error: ' : 'Comments: '}
                {notification.message && notification.message.length > 50
                  ? `${notification.message.slice(0, 50)}...`
                  : notification.message}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 11 }}>
                {notification.whenStr}
              </Typography>
            </Stack>
          </Grid>
          <Grid>
            <IconButton
              size="small"
              onClick={handleClose}
              aria-label="Dismiss notification"
              sx={{ transform: 'translate(6px, -8px)' }}
            >
              <CancelRoundedIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Stack>
      </Box>
    </Box>
  );
};

export default NotificationToast;
